import { useEffect, useMemo, useState } from "react";
import { Sidebar as ReactProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  Tooltip,
  Zoom,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { getYourProfile } from "../../services/UserService";

const Item = ({ title, to, icon, selected, onItemClick, isCollapsed }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Tooltip
      title={isCollapsed ? title : ""}
      placement="right"
      TransitionComponent={Zoom}
      arrow
      disableInteractive
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: colors.primary[600],
            color: colors.gray[100],
            fontSize: "13px",
            fontWeight: 600,
            padding: "8px 12px",
            borderRadius: "6px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
          },
        },
        arrow: {
          sx: {
            color: colors.primary[600],
          },
        },
      }}
    >
      <MenuItem
        active={selected === title}
        style={{ color: colors.gray[100] }}
        onClick={() => onItemClick(title)}
        icon={icon}
        component={<Link to={to} />}
      >
        <Typography
          sx={{
            display: isCollapsed ? "none" : "block", // Instantly hides text when collapsed
            fontWeight: selected === title ? 700 : 500,
            letterSpacing: "0.3px",
            fontSize: "0.85rem",
            whiteSpace: "nowrap", // Prevents text from wrapping to a new line
            overflow: "hidden", // Clips any stray pixels
          }}
        >
          {title}
        </Typography>
      </MenuItem>
    </Tooltip>
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState("");
  const [userInfo, setUserInfo] = useState({ fullName: "", role: "" });
  const [isLoading, setIsLoading] = useState(true); // Tracks profile & menu load state

  const [isCollapsed, setIsCollapsed] = useState(false); // Desktop collapse state
  const [toggled, setToggled] = useState(false); // Mobile overlay state
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Detects mobile view (< 600px)

  // Computes the actual visual state to prevent desktop collapse rules from breaking the mobile overlay menu
  const actualCollapsed = isCollapsed && !isMobile;
  const skeletonColor =
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.12)"
      : "rgba(0,0,0,0.1)";

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    } else {
      const fetchUserInfo = async () => {
        try {
          const response = await getYourProfile();

          if (response && response.data) {
            setUserInfo({
              fullName: response.data.fullName,
              role: response.data.role,
            });
          } else {
            console.error("User info not found");
          }
        } catch (error) {
          console.error("Failed to fetch user info:", error);
        } finally {
          setIsLoading(false); // Stop showing skeleton once fetch finishes
        }
      };
      fetchUserInfo();
    }
  }, [navigate]);

  useEffect(() => {
    switch (location.pathname) {
      case "/dashboard":
        setSelected("Dashboard");
        break;
      case "/sales":
        setSelected("Sales");
        break;
      case "/expenses":
        setSelected("Expenses");
        break;
      case "/users":
        setSelected("Users");
        break;
      case "/customers":
        setSelected("Customers");
        break;
      case "/inventory":
        setSelected("Inventory");
        break;
      case "/analytics":
        setSelected("Analytics");
        break;
      default:
        setSelected("");
        break;
    }
  }, [location.pathname]);

  // Handler to auto-close sidebar when an item is clicked
  const handleItemClick = (title) => {
    setSelected(title);
    if (isMobile) {
      setToggled(false); // Hide the mobile overlay menu
    }
  };

  // Memoized so this large style object is only rebuilt when the
  // values it actually depends on change, instead of on every render.
  const sidebarSx = useMemo(
    () => ({
      height: "100%",
      "& .ps-sidebar-root": {
        borderRight: "none !important", // Let container handle the border
      },
      "& .ps-sidebar-container": {
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%) !important`
            : `linear-gradient(180deg, ${colors.primary[400]} 0%, ${colors.primary[600]} 100%) !important`, // Enhanced light mode gradient
        borderRight: `1px solid ${theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[600]}`,
        boxShadow: actualCollapsed ? "none" : "4px 0 24px rgba(0,0,0,0.02)",
        overflowY: "auto",
        // Hardware-acceleration hint kept for the cosmetic properties below.
        willChange: "background, box-shadow",
        transition: "background 0.3s ease, box-shadow 0.3s ease !important",
        /* Completely hide the scrollbar across all major browsers */
        "&::-webkit-scrollbar": { display: "none" },
        msOverflowStyle: "none" /* IE and Edge */,
        scrollbarWidth: "none" /* Firefox */,
      },
      "& .ps-menu-icon": {
        backgroundColor: "transparent !important",
        margin: actualCollapsed ? "0 !important" : "0 10px 0 0 !important", // Resets margins completely when collapsed
        width: actualCollapsed ? "100% !important" : "auto !important",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      "& .ps-menu-label": {
        display: actualCollapsed ? "none !important" : "block", // Guarantees the label wrapper takes up zero space
      },
      "& .ps-menu-button": {
        padding: actualCollapsed ? "0 !important" : "10px 20px !important",
        margin: actualCollapsed ? "8px auto !important" : "4px 16px !important",
        width: actualCollapsed ? "48px !important" : "auto",
        height: actualCollapsed ? "48px !important" : "auto",
        borderRadius: "8px !important",
        justifyContent: actualCollapsed
          ? "center !important"
          : "flex-start !important",
        alignItems: "center !important",
        transition:
          "background-color 0.2s ease, transform 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important", // Sped up transform for snappy clicks
      },
      "& .ps-menu-button:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? `rgba(99, 102, 241, 0.08) !important`
            : `rgba(79, 70, 229, 0.08) !important`,
        color: `${colors.blueAccent[500]} !important`,
        transform: actualCollapsed ? "none" : "translateX(4px)",
      },
      /* Tactile Press Feel for all standard buttons */
      "& .ps-menu-button:active": {
        transform: actualCollapsed
          ? "scale(0.92) !important"
          : "scale(0.96) translateX(4px) !important",
      },
      "& .ps-menuitem-root.ps-active .ps-menu-button": {
        background: `linear-gradient(90deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[500]} 100%) !important`,
        color: `#ffffff !important`,
        boxShadow: `0px 4px 15px ${theme.palette.mode === "dark" ? "rgba(99, 102, 241, 0.35)" : "rgba(79, 70, 229, 0.35)"}`,
      },

      /* Specific Overrides for the Logo / Header Item */
      "& .logo-menu-item .ps-menu-button": {
        cursor: actualCollapsed ? "pointer !important" : "default !important",
      },
      "& .logo-menu-item .ps-menu-button:hover": {
        backgroundColor: actualCollapsed
          ? theme.palette.mode === "dark"
            ? "rgba(99, 102, 241, 0.08) !important"
            : "rgba(79, 70, 229, 0.08) !important"
          : "transparent !important", // Remove background hover if expanded
        transform: "none !important", // Remove slide-right hover if expanded
      },
      "& .logo-menu-item .ps-menu-button:active": {
        transform: actualCollapsed
          ? "scale(0.92) !important"
          : "none !important", // Remove scale click effect if expanded
      },
    }),
    [theme, colors, actualCollapsed],
  );

  return (
    <Box sx={sidebarSx}>
      {/* Mobile Floating Toggle Button */}
      {isMobile && (
        <IconButton
          onClick={() => setToggled(true)}
          sx={{
            display: toggled ? "none" : "flex",
            position: "fixed",
            bottom: 60,
            right: 30,
            zIndex: 1000,
            background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
            color: "#ffffff",
            boxShadow: "0 6px 16px rgba(0,0,0,0.3)",
            "&:hover": {
              background: `linear-gradient(135deg, ${colors.blueAccent[400]} 0%, ${colors.blueAccent[500]} 100%)`,
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease",
            width: 55,
            height: 55,
          }}
        >
          <MenuOutlinedIcon fontSize="medium" />
        </IconButton>
      )}

      <ReactProSidebar
        collapsed={actualCollapsed}
        toggled={toggled}
        breakPoint="sm"
        onBackdropClick={() => setToggled(false)}
        style={{ height: "100vh" }}
      >
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            className="logo-menu-item"
            onClick={() => {
              if (actualCollapsed) {
                setIsCollapsed(false); // Expand on desktop if currently collapsed
              }
            }}
            icon={actualCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 30px 0",
              color: colors.gray[100],
            }}
          >
            {!actualCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
                mr="15px"
              >
                <Typography
                  variant="h3"
                  color={colors.gray[100]}
                  sx={{ letterSpacing: "1.5px", fontWeight: 800 }}
                >
                  BFB<span style={{ color: colors.blueAccent[500] }}>.</span>
                </Typography>

                {/* Desktop Collapse / Mobile Close Button */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation(); // Prevents the parent MenuItem click event from firing simultaneously
                    if (isMobile) {
                      setToggled(false);
                    } else {
                      setIsCollapsed(true);
                    }
                  }}
                  sx={{
                    transition:
                      "transform 0.3s ease, background-color 0.2s ease",
                    borderRadius: "8px",
                    "&:hover": {
                      transform: "rotate(90deg)",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {}
          {/* Skeleton Loaders OR Real Content */}
          {isLoading ? (
            <Box
              sx={{
                px: actualCollapsed ? 0 : 2,
                py: 2,
                animation: "fadeIn 0.5s ease-in-out",
              }}
            >
              {/* Profile Skeletons */}
              {!actualCollapsed && (
                <Box
                  mb="35px"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Skeleton
                    variant="circular"
                    width={80}
                    height={80}
                    sx={{ bgcolor: skeletonColor }}
                  />
                  <Skeleton
                    variant="text"
                    width={140}
                    height={28}
                    sx={{ mt: 1.5, bgcolor: skeletonColor }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={80}
                    height={24}
                    sx={{ mt: 1, borderRadius: "20px", bgcolor: skeletonColor }}
                  />
                </Box>
              )}

              {/* Menu Items Skeletons */}
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <Box
                    key={i}
                    display="flex"
                    alignItems="center"
                    justifyContent={actualCollapsed ? "center" : "flex-start"}
                    sx={{ px: actualCollapsed ? 0 : 1 }}
                  >
                    <Skeleton
                      variant="rounded"
                      width={actualCollapsed ? 48 : 24}
                      height={actualCollapsed ? 48 : 24}
                      sx={{ borderRadius: "8px", bgcolor: skeletonColor }}
                    />
                    {!actualCollapsed && (
                      <Skeleton
                        variant="text"
                        width="65%"
                        height={24}
                        sx={{ ml: 2, bgcolor: skeletonColor }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <>
              {/* Profile Box */}
              {!actualCollapsed && (
                <Box mb="35px" sx={{ animation: "fadeIn 0.5s ease-in-out" }}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        padding: "4px",
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${colors.blueAccent[400]} 0%, ${colors.greenAccent[400]} 100%)`,
                        transition:
                          "transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                          boxShadow: `0px 8px 24px ${theme.palette.mode === "dark" ? "rgba(99, 102, 241, 0.4)" : "rgba(79, 70, 229, 0.3)"}`,
                        },
                      }}
                    >
                      <img
                        src={`../../assets/user.png`}
                        alt="user-profile"
                        width="80px"
                        height="80px"
                        style={{
                          cursor: "pointer",
                          borderRadius: "50%",
                          border: `3px solid ${theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff"}`,
                          display: "block",
                        }}
                      />
                    </Box>
                  </Box>

                  <Box textAlign="center" mt="15px">
                    <Typography
                      variant="h4"
                      color={colors.gray[100]}
                      fontWeight="800"
                      sx={{ letterSpacing: "-0.5px" }}
                    >
                      {userInfo.fullName || "User"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={
                        userInfo.role === "ADMIN"
                          ? colors.blueAccent[500]
                          : colors.greenAccent[500]
                      }
                      sx={{
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        marginTop: "4px",
                        display: "inline-block",
                        backgroundColor:
                          userInfo.role === "ADMIN"
                            ? theme.palette.mode === "dark"
                              ? "rgba(99, 102, 241, 0.15)"
                              : "rgba(79, 70, 229, 0.1)"
                            : theme.palette.mode === "dark"
                              ? "rgba(76, 206, 172, 0.15)"
                              : "rgba(5, 150, 105, 0.1)",
                        padding: "4px 10px",
                        borderRadius: "20px",
                      }}
                    >
                      {userInfo.role || "Employee"}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Menu Links Box */}
              <Box sx={{ pb: "30px", animation: "fadeIn 0.5s ease-in-out" }}>
                <Item
                  title="Dashboard"
                  to="/dashboard"
                  icon={<HomeOutlinedIcon />}
                  selected={selected}
                  onItemClick={handleItemClick}
                  isCollapsed={actualCollapsed}
                />

                {!actualCollapsed && (
                  <Typography
                    variant="caption"
                    color={colors.gray[400]}
                    m="25px 0 10px 20px"
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.15em",
                      fontWeight: 700,
                      display: "block",
                    }}
                  >
                    Transactions
                  </Typography>
                )}

                <Item
                  title="Sales"
                  to="/sales"
                  icon={<ReceiptOutlinedIcon />}
                  selected={selected}
                  onItemClick={handleItemClick}
                  isCollapsed={actualCollapsed}
                />
                <Item
                  title="Expenses"
                  to="/expenses"
                  icon={<MonetizationOnOutlinedIcon />}
                  selected={selected}
                  onItemClick={handleItemClick}
                  isCollapsed={actualCollapsed}
                />

                {!actualCollapsed && (
                  <Typography
                    variant="caption"
                    color={colors.gray[400]}
                    m="25px 0 10px 20px"
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.15em",
                      fontWeight: 700,
                      display: "block",
                    }}
                  >
                    Manage
                  </Typography>
                )}

                {userInfo.role === "ADMIN" && (
                  <Item
                    title="Users"
                    to="/users"
                    icon={<PeopleOutlinedIcon />}
                    selected={selected}
                    onItemClick={handleItemClick}
                    isCollapsed={actualCollapsed}
                  />
                )}

                <Item
                  title="Customers"
                  to="/customers"
                  icon={<ContactsOutlinedIcon />}
                  selected={selected}
                  onItemClick={handleItemClick}
                  isCollapsed={actualCollapsed}
                />
                <Item
                  title="Inventory"
                  to="/inventory"
                  icon={<Inventory2OutlinedIcon />}
                  selected={selected}
                  onItemClick={handleItemClick}
                  isCollapsed={actualCollapsed}
                />

                {!actualCollapsed && userInfo.role === "ADMIN" && (
                  <Typography
                    variant="caption"
                    color={colors.gray[400]}
                    m="25px 0 10px 20px"
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.15em",
                      fontWeight: 700,
                      display: "block",
                    }}
                  >
                    Reports
                  </Typography>
                )}

                {userInfo.role === "ADMIN" && (
                  <Item
                    title="Analytics"
                    to="/analytics"
                    icon={<BarChartOutlinedIcon />}
                    selected={selected}
                    onItemClick={handleItemClick}
                    isCollapsed={actualCollapsed}
                  />
                )}
              </Box>
            </>
          )}
        </Menu>
      </ReactProSidebar>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

export default Sidebar;
